import styled from 'styled-components';
import SVG from 'react-inlinesvg';
import { themeGet } from '@styled-system/theme-get';
import { useTranslation } from 'react-i18next';

import { Delete } from '../../../../assets';
import useDropdown from '../../../../lib/hooks/useDropdown';
import Modal from '../../../../shared/Modal/Modal.jsx';
import ModalConfirmation from '../../../../shared/ModalConfirmation/ModalConfirmation.jsx';
import {
  useDeleteTileMutation,
  useCopyTileMutation,
  useUpdateTileMutation,
} from './hooks/useTileMutation.jsx';
import Text from '../../../../shared/Text';
import { useToggleStatus } from './hooks/useToggleStatus.jsx';
import { useCampaignContext } from '../../contexts/CampaignContext.jsx';

export const TileCardMenu = ({
  tileId,
  tileHeadline,
  campaignId,
  tileStatus,
  postStatus,
}) => {
  const { campaign } = useCampaignContext();
  const isDisabled = campaign.isArchived || !!campaign.deletedAt;
  const { t } = useTranslation('tiles');
  const { deleteTileMutation } = useDeleteTileMutation({
    tileHeadline,
    campaignId,
  });
  const { copyTileMutation } = useCopyTileMutation({
    tileHeadline,
  });

  const { updateTileMutation } = useUpdateTileMutation({
    tileHeadline,
    campaignId,
  });

  const { toggle, toggleText, toggleIcon, canToggle } = useToggleStatus({
    postStatus,
    tileStatus,
  });

  const {
    dropdownRef,
    buttonRef,
    showDropdown,
    setShowDropdown,
  } = useDropdown();

  const deleteTileModal = () =>
    Modal({
      component: (
        <ModalConfirmation
          header={t('removeModal.headerText')}
          body={t('removeModal.bodyText')}
          cancelText={t('removeModal.cancel')}
          submitText={t('removeModal.submit')}
          variant="error"
          onSave={() => {
            deleteTileMutation(tileId);
            setShowDropdown(false);
          }}
        />
      ),
      width: '375px',
      padding: '17px',
    });

  return (
    <Container showDropdown={showDropdown}>
      <IconWrapper
        data-testid="icon-wrapper"
        onClick={() => setShowDropdown(!showDropdown)}
        ref={buttonRef}
      >
        <MenuIcon />
      </IconWrapper>
      {showDropdown && (
        <Menu ref={dropdownRef}>
          {!isDisabled && (
            <Item onClick={() => deleteTileModal()}>
              <DeleteIcon src={Delete} />
              <Text variant="small2">{t('tileCardMenu.delete')}</Text>
            </Item>
          )}
          {!isDisabled && canToggle.includes(tileStatus) && (
            <Item
              onClick={() => {
                updateTileMutation({ tileId, tileStatus: toggle });
                setShowDropdown(false);
              }}
            >
              {toggleIcon}
              <Text variant="small2">{toggleText}</Text>
            </Item>
          )}
          <Item
            onClick={() => {
              copyTileMutation(tileId);
              setShowDropdown(false);
            }}
          >
            <CopyIcon />
            <Text variant="small2">{t('tileCardMenu.copy')}</Text>
          </Item>
        </Menu>
      )}
    </Container>
  );
};

const IconWrapper = styled.div`
  cursor: pointer;
  &:hover {
    color: ${themeGet('colors.primary.100')};
  }
`;

const Container = styled.div`
  width: 36px;
  height: 36px;
  background: ${themeGet('colors.white')};
  border: 1px solid ${themeGet('colors.black.10')};
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: center;
  visibility: ${props => (props.showDropdown ? 'visible' : 'inherit')};
  border-radius: 50%;

  &:hover {
    border: 1px solid ${themeGet('colors.primary.100')};
  }
`;

const MenuIcon = styled.i.attrs({
  className: 'fa-solid fa-ellipsis',
})`
  font-size: 24px;
`;

const CopyIcon = styled.i.attrs({
  className: 'fa-regular fa-copy',
})`
  margin-right: 5px;
`;

const DeleteIcon = styled(SVG)`
  width: 16px;
  height: 16px;
  margin-right: 5px;
`;

const Menu = styled.ul`
  position: absolute;
  right: 0px;
  top: 30px;
  box-shadow: 0px 0px 8px rgba(37, 40, 43, 0.12);
  border-radius: 8px;
  width: 140px;
  background: white;
  z-index: 1;
`;

const Item = styled.li`
  padding: 10px;
  cursor: pointer;
  display: flex;
  align-items: center;

  &:hover {
    background: ${themeGet('colors.primary.10')};
    color: ${themeGet('colors.primary.100')};

    span {
      color: ${themeGet('colors.primary.100')};
    }

    svg {
      path {
        stroke: ${themeGet('colors.primary.100')};
      }
    }
  }

  &:first-of-type {
    border-radius: 8px 8px 0 0;
  }

  &:last-of-type {
    border-radius: 0 0 8px 8px;
  }

  span {
    margin: 5px;
  }
`;
